/**
 * La mappa delle aziende intera (27/10/2026): la carta occupa quasi tutto lo spazio, sopra ha titolo e filtri, accanto
 * (o sotto, sul telefono) il pannello col riepilogo o con la scheda dell'azienda scelta. La usano tutte e due le pagine:
 * la console (`modo: 'console'`, con stati, sede, abbonamento e il passaggio alla scheda) e il link condiviso
 * (`modo: 'condivisa'`, con la sola parte pubblica arrivata dal server).
 *
 * La console non ha l'assistente Yuma (vive nel gestionale): qui non si registrano comandi.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LIVELLI_MAPPA, STATI_MAPPA, type LivelloMappa, type StatoMappa } from '@shared/admin/mappaAziende';
import { tokens } from '../../theme';
import { COLORI_MAPPA, FOGLIO_CHIUSO, LARGHEZZA_PANNELLO, PUNTO_TELEFONO } from './aspetto';
import { ElencoAziende, ordinaPerAccount } from './ElencoAziende';
import { FiltriMappa } from './FiltriMappa';
import { FoglioMobile } from './FoglioMobile';
import { useFormatiMappa } from './formati';
import { LogoAzienda } from './LogoAzienda';
import { PallinoAdesso } from './PallinoAdesso';
import { MappaItalia, type SegnoDaDisegnare } from './MappaItalia';
import { PannelloRiepilogo, totaliDelleAziende } from './PannelloRiepilogo';
import { SchedaAzienda } from './SchedaAzienda';
import type { AziendaNellaVista, ModoMappa } from './tipi';

interface Props {
  modo: ModoMappa;
  aziende: readonly AziendaNellaVista[];
  giorniPersoneAttive: number;
  minutiAdesso: number;
  /** Il titolo in alto a sinistra sopra la carta (con l'interruttore elenco/mappa nella console). */
  intestazione: React.ReactNode;
  /** I bottoni in alto a destra sopra la carta (condividi, aggiorna). */
  azioni?: React.ReactNode;
  /** Le righe piccole in fondo al pannello: da quando sono i dati, la fonte dei confini. */
  piede: React.ReactNode;
  onApriAzienda?: (tenantId: string) => void;
}

const statoDi = (a: AziendaNellaVista): StatoMappa => a.stato ?? 'attiva';

export const VistaMappaAziende: React.FC<Props> = ({ modo, aziende, giorniPersoneAttive, minutiAdesso, intestazione, azioni, piede, onApriAzienda }) => {
  const { t } = useTranslation('mappa');
  const theme = useTheme();
  const telefono = useMediaQuery(theme.breakpoints.down(PUNTO_TELEFONO));
  const formati = useFormatiMappa();

  const [stati, setStati] = useState<Set<StatoMappa>>(() => new Set<StatoMappa>(['attiva']));
  const [livelli, setLivelli] = useState<Set<LivelloMappa>>(() => new Set(LIVELLI_MAPPA));
  const [soloAdesso, setSoloAdesso] = useState(false);
  const [selezionata, setSelezionata] = useState<string | null>(null);
  const [gruppo, setGruppo] = useState<string[] | null>(null);
  const [foglioAperto, setFoglioAperto] = useState(false);
  const [confiniAssenti, setConfiniAssenti] = useState(false);

  // I conteggi dei filtri: gli stati contano tutte le aziende, i livelli quelle degli stati scelti.
  const contiStati = useMemo(() => {
    const conti = Object.fromEntries(STATI_MAPPA.map((s) => [s, 0])) as Record<StatoMappa, number>;
    for (const a of aziende) conti[statoDi(a)] += 1;
    return conti;
  }, [aziende]);
  const negliStati = useMemo(() => aziende.filter((a) => stati.has(statoDi(a))), [aziende, stati]);
  const contiLivelli = useMemo(() => {
    const conti = Object.fromEntries(LIVELLI_MAPPA.map((l) => [l, 0])) as Record<LivelloMappa, number>;
    for (const a of negliStati) conti[a.livello] += 1;
    return conti;
  }, [negliStati]);
  const aziendeAdesso = useMemo(() => negliStati.filter((a) => a.persone_adesso > 0).length, [negliStati]);
  const tutte = useMemo(() => new Map(aziende.map((a) => [a.chiave, a])), [aziende]);
  const visibili = useMemo(() => {
    const base = negliStati.filter((a) => livelli.has(a.livello) && (!soloAdesso || a.persone_adesso > 0));
    if (soloAdesso) return base;
    // Il fornitore di un'azienda in vista si vede anche se il suo stato è filtrato: Forniture Fotovoltaiche ha l'account
    // bloccato (non usa il gestionale) ma rifornisce sette aziende, e il legame è la cosa da mostrare (28/10/2026).
    const presenti = new Set(base.map((a) => a.chiave));
    const fornitori: AziendaNellaVista[] = [];
    for (const a of base) for (const f of a.fornitori ?? []) {
      const fornitore = tutte.get(f);
      if (!fornitore || presenti.has(f) || !livelli.has(fornitore.livello)) continue;
      presenti.add(f);
      fornitori.push(fornitore);
    }
    return [...base, ...fornitori];
  }, [negliStati, livelli, soloAdesso, tutte]);

  const perChiave = useMemo(() => new Map(visibili.map((a) => [a.chiave, a])), [visibili]);
  /**
   * Gli archi: chi ha PORTATO chi su YumaOS (programma partner), dall'azienda del partner, fra le aziende in vista. Non
   * chi compra da chi: Vivisolar e Power Service comprano da Forniture Fotovoltaiche ma non le ha portate lei (Simone,
   * 28/10/2026). Solo nella console.
   */
  const legami = useMemo(
    () => visibili.flatMap((a) => {
      const da = a.portata_da?.azienda;
      return da && perChiave.has(da) ? [{ da, verso: a.chiave }] : [];
    }),
    [visibili, perChiave],
  );
  const scelta = selezionata ? perChiave.get(selezionata) ?? null : null;

  // Un'azienda che un filtro ha tolto non resta scelta.
  useEffect(() => {
    if (selezionata && !perChiave.has(selezionata)) setSelezionata(null);
    if (gruppo && gruppo.some((k) => !perChiave.has(k))) setGruppo(null);
  }, [perChiave, selezionata, gruppo]);

  // Un segno per sede: tutte quelle di un'azienda rimandano a lei.
  const daDisegnare: SegnoDaDisegnare[] = useMemo(() => visibili.flatMap((a) => a.sedi.map((s) => ({
      chiave: `${a.chiave}|${s.chiave}`,
      azienda: a.chiave,
      principale: s.principale,
      nome: a.nome,
      nome_sede: s.nome,
      citta: s.citta,
      lng: s.posizione.lng,
      lat: s.posizione.lat,
      livello: a.livello,
      account: a.account,
      account_totale: a.account_totale,
      persone_attive: a.persone_attive,
      persone_adesso: a.persone_adesso,
      colore: a.colore,
      logo: a.logo_quadrato_url ?? null,
      spenta: statoDi(a) !== 'attiva',
    }))), [visibili]);
  const [vaiAllaSede, setVaiAllaSede] = useState<{ chiave: string; n: number } | null>(null);

  // Sul telefono la scelta NON apre il foglio: l'azienda compare nella sua riga chiusa e la carta resta in vista;
  // la scheda intera si apre toccando quella riga.
  const scegli = useCallback((chiave: string | null) => {
    setSelezionata(chiave);
    if (!chiave) setGruppo(null);
  }, []);

  const apriGruppo = useCallback((chiavi: string[]) => {
    setSelezionata(null);
    setGruppo(chiavi);
    setFoglioAperto(true);
  }, []);

  const confiniNonDisponibili = useCallback(() => setConfiniAssenti(true), []);

  // L'altezza della testata sopra la carta: l'Italia si inquadra sotto, non dietro i filtri.
  const testata = useRef<HTMLDivElement>(null);
  const [altezzaTestata, setAltezzaTestata] = useState(120);
  useLayoutEffect(() => {
    const el = testata.current;
    if (!el) return;
    const misura = () => setAltezzaTestata(Math.ceil(el.getBoundingClientRect().height));
    misura();
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(el);
    return () => osservatore.disconnect();
  }, []);

  const margini = useMemo(
    () => ({ alto: altezzaTestata + 12, destra: telefono ? 12 : 72, basso: telefono ? FOGLIO_CHIUSO + 16 : 28, sinistra: telefono ? 12 : 28 }),
    [altezzaTestata, telefono],
  );

  const totali = totaliDelleAziende(visibili);
  const nessuna = visibili.length === 0;

  const contenutoPannello = scelta ? (
    <SchedaAzienda
      azienda={scelta}
      modo={modo}
      giorni={giorniPersoneAttive}
      formati={formati}
      onTorna={() => setSelezionata(null)}
      onApri={onApriAzienda}
      fornitori={(scelta.fornitori ?? []).map((f) => ({ chiave: f, nome: tutte.get(f)?.nome ?? f, inVista: perChiave.has(f) }))}
      portataDa={scelta.portata_da ? {
        partner: scelta.portata_da.partner,
        azienda: scelta.portata_da.azienda
          ? { chiave: scelta.portata_da.azienda, nome: tutte.get(scelta.portata_da.azienda)?.nome ?? scelta.portata_da.azienda, inVista: perChiave.has(scelta.portata_da.azienda) }
          : null,
      } : null}
      portate={aziende.filter((a) => a.portata_da?.azienda === scelta.chiave).map((a) => ({ chiave: a.chiave, nome: a.nome, inVista: perChiave.has(a.chiave) }))}
      clienti={aziende.filter((a) => a.fornitori?.includes(scelta.chiave)).map((a) => ({ chiave: a.chiave, nome: a.nome, inVista: perChiave.has(a.chiave) }))}
      onScegliAzienda={(chiave) => setSelezionata(chiave)}
      onVaiAllaSede={(sede) => {
        setVaiAllaSede((prima) => ({ chiave: `${scelta.chiave}|${sede}`, n: (prima?.n ?? 0) + 1 }));
        setFoglioAperto(false);
      }}
    />
  ) : gruppo ? (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, pt: 1.5, pb: 1 }}>
        <Tooltip title={t('scheda.torna')}>
          <IconButton onClick={() => setGruppo(null)} aria-label={t('scheda.torna')} sx={{ width: 44, height: 44 }}><ArrowBack fontSize="small" /></IconButton>
        </Tooltip>
        <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.1rem' }}>
          {t('pannello.stesso_punto', { count: gruppo.length })}
        </Typography>
      </Box>
      <ElencoAziende
        aziende={ordinaPerAccount(gruppo.map((k) => perChiave.get(k)).filter((a): a is AziendaNellaVista => !!a))}
        selezionata={selezionata}
        onScegli={(k) => setSelezionata(k)}
        formati={formati}
      />
    </Box>
  ) : (
    <PannelloRiepilogo aziende={visibili} giorni={giorniPersoneAttive} minutiAdesso={minutiAdesso} conLegami={legami.length > 0} selezionata={selezionata} onScegli={scegli} formati={formati} />
  );

  const pannello = (
    <>
      {contenutoPannello}
      <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${tokens.color.line}`, '& p': { fontSize: '0.72rem', color: tokens.color.mist, lineHeight: 1.5 } }}>
        {confiniAssenti && <Typography sx={{ mb: 0.5 }}>{t('pagina.confini_assenti')}</Typography>}
        {piede}
      </Box>
    </>
  );

  const anteprima = scelta ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <LogoAzienda nome={scelta.nome} logoUrl={scelta.logo_quadrato_url ?? scelta.logo_url} colore={scelta.colore} dimensione={40} />
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.05rem' }}>{scelta.nome}</Typography>
        <Typography noWrap sx={{ fontSize: '0.78rem', color: tokens.color.ash }}>
          {[
            scelta.citta,
            t('scheda.account_totale', { count: scelta.account_totale }),
            scelta.persone_adesso > 0
              ? t('riepilogo.numero_adesso', { adesso: formati.numero(scelta.persone_adesso) })
              : t('riepilogo.al_lavoro', { attive: formati.numero(scelta.persone_attive) }),
          ].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2.25, flexWrap: 'wrap', rowGap: 0.25 }}>
      {[
        { n: totali.aziende, testo: t('riepilogo.aziende', { count: totali.aziende }) },
        { n: totali.account, testo: t('riepilogo.account', { count: totali.account }) },
        { n: totali.attive, testo: t('riepilogo.al_lavoro_breve') },
        { n: totali.adesso, testo: t('riepilogo.adesso_breve'), adesso: true },
      ].map((v) => (
        <Box key={v.testo} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
          {'adesso' in v && <Box sx={{ alignSelf: 'center', display: 'flex' }}><PallinoAdesso /></Box>}
          <Typography component="span" sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.5rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {formati.numero(v.n)}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.8rem', color: tokens.color.ash }}>{v.testo}</Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ position: 'relative', display: 'flex', height: '100%', width: '100%', overflow: 'hidden', bgcolor: COLORI_MAPPA.mare }}>
      <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <MappaItalia
          segni={daDisegnare}
          legami={legami}
          vaiAllaSede={vaiAllaSede}
          selezionata={selezionata}
          onSeleziona={scegli}
          onGruppo={apriGruppo}
          margini={margini}
          spazioControlliBasso={telefono ? FOGLIO_CHIUSO : 0}
          onConfiniNonDisponibili={confiniNonDisponibili}
        />

        {/* La testata sopra la carta: non cattura i gesti fuori dai suoi controlli. */}
        <Box
          ref={testata}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            px: { xs: 1.5, md: 3 },
            pt: { xs: 1.5, md: 2.5 },
            pb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            pointerEvents: 'none',
            background: `linear-gradient(${COLORI_MAPPA.mare} 55%, transparent)`,
            '& > *': { pointerEvents: 'auto' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, justifyContent: 'space-between' }}>
            <Box sx={{ minWidth: 0 }}>{intestazione}</Box>
            {azioni && <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>{azioni}</Box>}
          </Box>
          <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
            <FiltriMappa
              mostraStati={modo === 'console'}
              contiStati={contiStati}
              stati={stati}
              onStati={setStati}
              contiLivelli={contiLivelli}
              livelli={livelli}
              onLivelli={setLivelli}
              aziendeAdesso={aziendeAdesso}
              soloAdesso={soloAdesso}
              onSoloAdesso={setSoloAdesso}
            />
          </Box>
        </Box>

        {nessuna && (
          <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', bgcolor: tokens.color.paper, border: `1px solid ${tokens.color.line}`, borderRadius: 2, px: 3, py: 2.5, maxWidth: 320 }}>
            <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 500, fontSize: '1.05rem', mb: 1.5 }}>{t('filtri.nessuna')}</Typography>
            <Button
              variant="outlined"
              onClick={() => { setStati(new Set(STATI_MAPPA)); setLivelli(new Set(LIVELLI_MAPPA)); setSoloAdesso(false); }}
              sx={{ textTransform: 'none', minHeight: 40, color: tokens.color.graphite, borderColor: tokens.color.graphite }}
            >
              {t('filtri.mostra_tutte')}
            </Button>
          </Box>
        )}

        {telefono && (
          <FoglioMobile aperto={foglioAperto} onCambia={setFoglioAperto} anteprima={anteprima}>
            {pannello}
          </FoglioMobile>
        )}
      </Box>

      {!telefono && (
        <Box
          component="aside"
          sx={{
            width: LARGHEZZA_PANNELLO,
            flexShrink: 0,
            bgcolor: tokens.color.paper,
            borderLeft: `1px solid ${tokens.color.line}`,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          }}
        >
          {pannello}
        </Box>
      )}
    </Box>
  );
};
